//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, RoleEnum } from '@shared/Enums'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { PoolControllerAbi } from '@shared/Abis'
import { AlertCTO } from '@shared/AlertCTO'

import { dateToEthDate } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { alertActions } from '@react/redux/alertSlice'
import { poolActions } from '@react/redux/poolSlice'
import { UserConnection } from '@shared/DeploymentConfigs'

export const initiateRolloverTx =
  (props: {
    poolAddr: EthAddr
    poolControllerAddr: EthAddr
    loanAddr: EthAddr
    priorLoanAddr: EthAddr
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.ROLLOVER_INITIATE
    try {
      logOT.debug('initiateRolloverSaga 1')
      const { poolAddr, poolControllerAddr, loanAddr, priorLoanAddr } = props
      const { webSocket } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      assertRole(roleEnum, RoleEnum.POOL_ADMIN)

      await initiateRollover(
        webSocket,
        signer as Signer,
        poolAddr,
        poolControllerAddr,
        loanAddr,
        priorLoanAddr,
        chainConfigurationName
      )

      dispatch(
        poolActions.initiateRolloverSuccess({
          poolAddr,
          loanAddr,
          priorLoanAddr,
        })
      )
      const successAlert: AlertCTO = {
        sourceObjectAddr: loanAddr,
        title: 'Rollover initiated.',
        description: '',
        createdAt: dateToEthDate(new Date()),
        dueDateTime: 0,
        alertLevel: AlertLevelEnum.SUCCESS,
      }
      dispatch(alertActions.addAlert(successAlert))
    } catch (e: any) {
      await dispatchError(dispatch, e, activity, 4003)
    }
  }
export const initiateRollover = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  poolControllerAddr: EthAddr,
  loanAddr: EthAddr,
  priorLoanAddr: EthAddr,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('initiateRollover')

  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.ROLLOVER_INITIATE, poolAddr, uuid, chainConfigurationName))

  const poolControllerContract = new Contract(poolControllerAddr, PoolControllerAbi, poolAdminSigner)

  const approveReceipt = await (await poolControllerContract.initiateRollover(loanAddr, priorLoanAddr)).wait(1)

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.ROLLOVER_INITIATE, poolAddr, uuid, approveReceipt.hash, chainConfigurationName)
  )
  logOT.debug('{ initiateRollover', approveReceipt)
}
